/**
 * KpiGrid.jsx
 *
 * Wrapper around the .kpi-grid CSS class that picks the column count based
 * on actual container width. Rule:
 *   - If all N cards fit at >= minCardWidth in one row, use N columns.
 *   - Otherwise use ceil(N/2) columns (2 balanced rows, ±1).
 *   - If even 2 rows can't fit, drop to ceil(N/3), and so on.
 *
 * Padding/gaps are pulled from the existing .kpi-grid styles (gap: 16px).
 */
import { Children, useEffect, useLayoutEffect, useRef, useState } from 'react';

const GAP = 16;
const DEFAULT_MIN_CARD_WIDTH = 150;

export default function KpiGrid({ children, className = '', minCardWidth = DEFAULT_MIN_CARD_WIDTH, style = {} }) {
  const ref = useRef(null);
  const n = Children.count(children);
  const [cols, setCols] = useState(n);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || n === 0) return;

    const measure = () => {
      const w = el.clientWidth;
      if (!w) return;
      let rows = 1;
      while (rows < n) {
        const c = Math.ceil(n / rows);
        if (c * minCardWidth + (c - 1) * GAP <= w) break;
        rows++;
      }
      setCols(Math.ceil(n / rows));
    };

    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [n, minCardWidth]);

  return (
    <div
      ref={ref}
      className={`kpi-grid${className ? ' ' + className : ''}`}
      style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, ...style }}
    >
      {children}
    </div>
  );
}
